import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import MemberPicker, { useMembersMap, memberName } from './MemberPicker.jsx'
import { Title, Card, Field, Err, money } from './gui.jsx'
import { watchProducts } from '../../lib/products.js'
import { watchProShopCredit, addTransaction, addGiftCardRedemption } from '../../lib/proShopCredit.js'
import { watchSettings } from '../../lib/settings.js'
import { watchSales, recordSale, recordReturn, isToday } from '../../lib/sales.js'
import { getCurrentEmployeeId } from '../../lib/roles.js'

const TENDER_TYPES = [
  { key: 'cash', label: 'Cash' },
  { key: 'card', label: 'Card (stub)' },
  { key: 'shopCredit', label: 'Shop / Tournament Credit' },
  { key: 'giftCard', label: 'Gift Card' },
]

export default function GPOS() {
  const [products, setProducts] = useState({})
  const [credit, setCredit] = useState({})
  const [settings, setSettings] = useState({ taxRate: 0 })
  const [sales, setSales] = useState({})
  const members = useMembersMap()
  const [cart, setCart] = useState([])
  const [memberId, setMemberId] = useState('')
  const [discount, setDiscount] = useState('')
  const [scan, setScan] = useState('')
  const [tenders, setTenders] = useState([])
  const [flash, setFlash] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    const a = watchProducts(setProducts), b = watchProShopCredit((v) => setCredit(v || {})), c = watchSettings(setSettings), d = watchSales(setSales)
    return () => { a(); b(); c(); d() }
  }, [])

  const prodList = useMemo(() => Object.values(products || {}).filter(Boolean), [products])
  const accounts = useMemo(() => Object.entries(credit.accounts || {}).map(([id, a]) => {
    const bal = Object.values(a.transactions || {}).reduce((s, t) => s + (t.type === 'redeem' ? -1 : 1) * (Number(t.amount) || 0), 0)
    return { id, name: a.name, balance: bal }
  }), [credit])
  const cards = useMemo(() => Object.entries(credit.giftCards || {}).map(([id, c]) => {
    const used = Object.values(c.redemptions || {}).reduce((s, r) => s + (Number(r.amount) || 0), 0)
    return { id, code: c.code, remaining: (Number(c.value) || 0) - used }
  }), [credit])

  const subtotal = cart.reduce((s, l) => s + l.qty * l.price, 0)
  const disc = Math.min(Number(discount) || 0, subtotal)
  const taxable = subtotal - disc
  const tax = Math.round(taxable * (Number(settings.taxRate) || 0) / 100 * 100) / 100
  const total = Math.round((taxable + tax) * 100) / 100
  const tendered = tenders.reduce((s, t) => s + (Number(t.amount) || 0), 0)
  const remaining = Math.round((total - tendered) * 100) / 100

  const addByScan = () => {
    const s = scan.trim().toLowerCase(); if (!s) return
    const p = prodList.find((p) => (p.barcode || '').toLowerCase() === s || (p.sku || '').toLowerCase() === s) || prodList.find((p) => `${p.name} ${p.sku} ${p.brand}`.toLowerCase().includes(s))
    if (p) addProduct(p); setScan('')
  }
  const addProduct = (p) => setCart((c) => { const i = c.findIndex((l) => l.productId === p.id); if (i >= 0) return c.map((l, j) => j === i ? { ...l, qty: l.qty + 1 } : l); return [...c, { productId: p.id, name: p.name || p.sku, qty: 1, price: Number(p.retail) || 0 }] })
  const setQty = (i, q) => setCart((c) => c.map((l, j) => j === i ? { ...l, qty: Math.max(1, Number(q) || 1) } : l))
  const removeLine = (i) => setCart((c) => c.filter((_, j) => j !== i))
  const addTender = (type) => setTenders((t) => [...t, { type, amount: remaining > 0 ? remaining.toFixed(2) : '', ref: '' }])
  const setTender = (i, patch) => setTenders((t) => t.map((x, j) => j === i ? { ...x, ...patch } : x))

  const complete = async () => {
    setErr('')
    if (!cart.length) { setErr('Cart is empty.'); return }
    if (remaining > 0.001) { setErr(`Still owed ${money(remaining)} — add a tender.`); return }
    try {
      for (const t of tenders) {
        const amt = Number(t.amount) || 0; if (amt <= 0) continue
        if (t.type === 'shopCredit' && t.ref) await addTransaction(t.ref, { type: 'redeem', amount: amt, reason: 'POS sale' })
        else if (t.type === 'giftCard' && t.ref) await addGiftCardRedemption(t.ref, { amount: amt, note: 'POS sale' })
        // t.type === 'card' → STUB: WIRE PROCESSOR HERE LATER (records amount, no charge)
        // t.type === 'cash' → nothing to process
      }
      await recordSale({ memberId, lines: cart, subtotal, discount: disc, tax, total, tenders: tenders.map((t) => ({ type: t.type, amount: Number(t.amount) || 0, ref: t.ref || null })), employeeId: getCurrentEmployeeId() })
      setCart([]); setTenders([]); setDiscount(''); setMemberId(''); setFlash('Sale complete ✓'); setTimeout(() => setFlash(''), 2500)
    } catch (e) { setErr(e?.message || 'Sale failed.') }
  }

  const todaySales = Object.values(sales || {}).filter((s) => s && isToday(s.timestamp)).sort((a, b) => b.timestamp - a.timestamp)
  const todayTotal = todaySales.reduce((s, x) => s + (Number(x.total) || 0), 0)

  return (
    <GShell>
      <Title title="Point of Sale" subtitle="Ring sales, split tenders, returns. Card tender is stubbed — no processor wired." />
      {flash ? <Card style={{ background: '#ECFDF5', borderColor: '#15803D55' }}><span className="text-sm font-bold" style={{ color: '#15803D' }}>{flash}</span></Card> : null}

      <div className="grid lg:grid-cols-2 gap-3">
        {/* Cart side */}
        <Card>
          <div className="flex gap-2">
            <input className="input flex-1" placeholder="Scan barcode / SKU or search…" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addByScan()} />
            <button className="btn-secondary text-sm" onClick={addByScan}>Add</button>
          </div>
          {scan.trim() ? (
            <div className="mt-1 max-h-32 overflow-y-auto text-sm border rounded" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              {prodList.filter((p) => `${p.name} ${p.sku} ${p.brand}`.toLowerCase().includes(scan.trim().toLowerCase())).slice(0, 8).map((p) => (
                <button key={p.id} className="block w-full text-left px-2 py-1 hover:bg-gray-50" onClick={() => { addProduct(p); setScan('') }}>{p.name || p.sku} · {money(p.retail)} <span className="text-gray-400">({p.quantity} in stock)</span></button>
              ))}
            </div>
          ) : null}
          <div className="mt-3 space-y-1">
            {cart.length === 0 ? <p className="text-sm text-gray-400">Cart is empty.</p> : cart.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm truncate">{l.name}</span>
                <input className="input w-14 text-center" type="number" value={l.qty} onChange={(e) => setQty(i, e.target.value)} />
                <span className="w-20 text-right text-sm">{money(l.qty * l.price)}</span>
                <button className="text-red-600 text-xs" onClick={() => removeLine(i)}>✕</button>
              </div>
            ))}
          </div>
        </Card>

        {/* Tender side */}
        <Card>
          <MemberPicker value={memberId} onChange={setMemberId} label="Member (optional)" />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Field label="Discount $"><input className="input w-full" type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} /></Field>
            <div className="text-sm self-end text-right">
              <div>Subtotal: {money(subtotal)}</div>
              {disc ? <div>Discount: −{money(disc)}</div> : null}
              <div>Tax ({settings.taxRate || 0}%): {money(tax)}</div>
              <div className="text-lg font-extrabold" style={{ color: 'var(--ge-primary)' }}>Total: {money(total)}</div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-1">
            {TENDER_TYPES.map((t) => <button key={t.key} className="btn-secondary text-[11px]" onClick={() => addTender(t.key)}>+ {t.label}</button>)}
          </div>
          <div className="mt-2 space-y-1">
            {tenders.map((t, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-xs w-28 shrink-0">{TENDER_TYPES.find((x) => x.key === t.type)?.label}</span>
                {t.type === 'shopCredit' ? (
                  <select className="input flex-1" value={t.ref} onChange={(e) => setTender(i, { ref: e.target.value })}><option value="">account…</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({money(a.balance)})</option>)}</select>
                ) : t.type === 'giftCard' ? (
                  <select className="input flex-1" value={t.ref} onChange={(e) => setTender(i, { ref: e.target.value })}><option value="">card…</option>{cards.filter((c) => c.remaining > 0).map((c) => <option key={c.id} value={c.id}>{c.code} ({money(c.remaining)})</option>)}</select>
                ) : t.type === 'card' ? <span className="text-[10px] text-amber-700 flex-1">stub — not charged</span> : <span className="flex-1" />}
                <input className="input w-20" type="number" step="0.01" placeholder="amt" value={t.amount} onChange={(e) => setTender(i, { amount: e.target.value })} />
                <button className="text-red-600 text-xs" onClick={() => setTenders((x) => x.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
          </div>
          <div className="text-sm mt-2 text-right" style={{ color: remaining > 0.001 ? '#B91C1C' : '#15803D' }}>
            {remaining > 0.001 ? `Remaining: ${money(remaining)}` : remaining < -0.001 ? `Change: ${money(-remaining)}` : 'Paid in full ✓'}
          </div>
          <button className="btn-primary w-full mt-2" onClick={complete} disabled={!cart.length}>Complete Sale</button>
          <Err msg={err} />
        </Card>
      </div>

      {/* Today's sales */}
      <section>
        <Title title="Sales Today" subtitle={`${todaySales.length} transaction${todaySales.length === 1 ? '' : 's'} · ${money(todayTotal)}`} />
        <div className="mt-2 space-y-1">
          {todaySales.length === 0 ? <p className="text-sm text-gray-400">No sales yet today.</p> : todaySales.map((s) => (
            <Card key={s.id} className="!p-3">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>{new Date(s.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {s.lines?.length || 0} item{(s.lines?.length || 0) === 1 ? '' : 's'} · {memberName(members, s.memberId)}{s.type === 'return' ? ' · RETURN' : ''}</span>
                <span className="flex items-center gap-2">
                  <span className="font-extrabold" style={{ color: s.total < 0 ? '#B91C1C' : 'var(--ge-primary)' }}>{money(s.total)}</span>
                  {s.type !== 'return' ? <button className="btn-ghost text-[11px]" onClick={async () => { if (window.confirm('Record a full return of this sale? Re-adds inventory.')) await recordReturn({ ...s, originalId: s.id }) }}>Return</button> : null}
                </span>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </GShell>
  )
}
